import { Product } from 'apis/offer-listing-service/types';
import { ItemDisplayUnit } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { SharedMenu } from 'modules/shared-menu/types';

import {
  CreateCategoryPayload,
  EditCategoryPayload,
  Offer,
  ProductMaintainFields,
  SharedMenuDietaryPreference,
  SharedMenuMandatoryWarning,
} from './types';
import { convertLocalIsoDateToUtcIsoDate, updatePrimaryLanguageTranslation } from './utils';
import { ValidCategoryFormValues } from './validationSchemas';
import { SelectionCategory } from '../../types';

export const fromAddCategoryFormValuesToApi = (
  validValues: ValidCategoryFormValues,
  primaryLanguage: Language,
  imageUrl: string | null,
): CreateCategoryPayload => ({
  name: updatePrimaryLanguageTranslation(primaryLanguage, validValues.name, undefined),
  description: validValues.description
    ? updatePrimaryLanguageTranslation(primaryLanguage, validValues.description, undefined)
    : undefined,
  image: imageUrl ?? undefined,
  is_wolt_special: validValues.isWoltSpecial,
});

export const fromEditCategoryFormValuesToApi = (
  category: SelectionCategory,
  validValues: ValidCategoryFormValues,
  primaryLanguage: Language,
  imageUrl: string | null,
): EditCategoryPayload => {
  const { version, id, scope, image, image_blurhash, subcategories, ...categoryAttrs } = category;
  return {
    ...categoryAttrs,
    name: updatePrimaryLanguageTranslation(primaryLanguage, validValues.name, category.name),
    description: validValues.description
      ? updatePrimaryLanguageTranslation(primaryLanguage, validValues.description, category.description)
      : undefined,
    image: imageUrl ?? undefined,
    is_wolt_special: validValues.isWoltSpecial,
    offer_bindings: category.offer_bindings && category.offer_bindings.length > 0 ? category.offer_bindings : undefined,
  };
};

export const buildUpdateSharedMenuInfoPayload = (scheduleValue: string | undefined, selection: SharedMenu) => {
  return {
    name: selection.name,
    primary_language: selection.primary_language,
    draft: {
      ...selection.draft,
      publish_at: scheduleValue ? convertLocalIsoDateToUtcIsoDate(scheduleValue) : undefined,
    },
    categoryIds: selection.categories.map(category => category.id),
  };
};

const transformProductImagesToOfferImages = (product: Product): Offer['images'] =>
  product.images?.map(({ url, blurhash }) => ({
    url,
    blurhash: blurhash ?? undefined,
  }));

export const buildProductOverriddenOffer = (offer: Offer, product?: Product): Offer => {
  if (!product) {
    return offer;
  }

  const isMaintainedByUser = (attr: ProductMaintainFields) => offer.product_maintain_fields?.includes(attr);
  return {
    ...offer,
    additives: isMaintainedByUser('additives') ? offer.additives : product.additives,
    alcohol_percentage: isMaintainedByUser('alcohol_permille')
      ? offer.alcohol_percentage
      : product.alcoholPermille
        ? product.alcoholPermille / 10
        : 0,
    allergens: isMaintainedByUser('allergens') ? offer.allergens : product.allergens,
    brand_id: isMaintainedByUser('brand_id') ? offer.brand_id : product.brandId ?? undefined,
    conditions_of_use_and_storage: isMaintainedByUser('conditions_of_use_and_storage')
      ? offer.conditions_of_use_and_storage
      : product.conditionsOfUseAndStorage ?? undefined,
    country_of_origin: isMaintainedByUser('country_of_origin')
      ? offer.country_of_origin
      : product.countryOfOrigin ?? undefined,
    description: isMaintainedByUser('description') ? offer.description : product.description,
    dietary_preferences: isMaintainedByUser('dietary_preferences')
      ? offer.dietary_preferences
      : product.dietaryPreferences?.map(preference => preference.toLowerCase() as SharedMenuDietaryPreference) || [],
    display_unit: isMaintainedByUser('display_unit')
      ? offer.display_unit
      : (product.displayUnit?.toLowerCase() as ItemDisplayUnit),
    distributor_information: isMaintainedByUser('distributor_information')
      ? offer.distributor_information
      : product.distributorInformation,
    image: isMaintainedByUser('images') ? offer.image : product.images.at(0)?.url,
    images: isMaintainedByUser('images') ? offer.images : transformProductImagesToOfferImages(product),
    ingredients: isMaintainedByUser('ingredients') ? offer.ingredients : product.ingredients,
    mandatory_warnings: isMaintainedByUser('mandatory_warnings')
      ? offer.mandatory_warnings
      : product.mandatoryWarnings?.map(warning => warning.toLowerCase() as SharedMenuMandatoryWarning) || [],
    more_information: isMaintainedByUser('more_information') ? offer.more_information : product.moreInformation,
    name: isMaintainedByUser('name') ? offer.name : product.name,
    number_of_units: isMaintainedByUser('number_of_units') ? offer.number_of_units : product.numberOfUnits ?? undefined,
    nutrition_facts: isMaintainedByUser('nutrition_facts')
      ? offer.nutrition_facts
      : product.nutritionFacts ?? undefined,
    nutrition_values_baseline: isMaintainedByUser('nutrition_values')
      ? offer.nutrition_values_baseline
      : (product.nutritionValues?.baseline.toLowerCase() as Offer['nutrition_values_baseline']),
    nutrition_values: isMaintainedByUser('nutrition_values')
      ? offer.nutrition_values
      : (product.nutritionValues?.nutritionValues || []).map(value => ({
          nutrition_value_id: value.nutritionValueId,
          amount: value.amount,
        })),
    producer_information: isMaintainedByUser('producer_information')
      ? offer.producer_information
      : product.producerInformation,
    volume_in_ml: isMaintainedByUser('volume_in_ml') ? offer.volume_in_ml : product.volumeInMl ?? undefined,
    weight_in_grams: isMaintainedByUser('weight_in_grams') ? offer.weight_in_grams : product.weightInGrams ?? undefined,
    product_hierarchy_level_id: product.productHierarchyLevelId,
    product_family_id: product.productFamilyId,
    barcode_gtin: product.gtin,
  };
};
